import Cancel from "@mui/icons-material/Cancel";
import CheckCircle from "@mui/icons-material/CheckCircle";
import { Box, Chip, Stack } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { WasteManifestSource } from "generated-graphql/graphql";

import { DOCUMENT_SOURCE_COLOR, SOURCE_LABEL, SOURCE_ORDER } from "../manifestSourceDisplay";
import { SectionHeader } from "./SectionHeader";

interface ManifestDataSourcesProps {
  /** Sources currently providing data for this manifest. */
  availableSources: readonly WasteManifestSource[];
}

/** Leading icon inherits the chip's text color (check when present, X when missing). */
const ICON_INHERIT_SX = { "& .MuiChip-icon": { color: "inherit" } } as const;

/**
 * Compact "Data Sources" panel inside the Summary box: a soft chip per data
 * source (e-Manifest / Vendor / Document) with a check icon, greyed with an X
 * when that source is missing. Spells out which sources back this manifest,
 * complementing the per-field source dots.
 */
export function ManifestDataSources({ availableSources }: ManifestDataSourcesProps) {
  const theme = useTheme();
  // Concrete color per source (no indigo palette role exists, so Document uses
  // an explicit indigo). The present chips tint themselves with the same soft
  // paradigm as StatusChip: colored label/icon on the color at 8% alpha.
  const sourceColor: Record<WasteManifestSource, string> = {
    [WasteManifestSource.EManifest]: theme.palette.success.main,
    [WasteManifestSource.Vendor]: theme.palette.info.main,
    [WasteManifestSource.ManifestDocument]: DOCUMENT_SOURCE_COLOR,
  };

  return (
    <Box>
      <SectionHeader title="Data Sources" />
      <Stack spacing={0.75} alignItems="flex-start">
        {SOURCE_ORDER.map((source) => {
          if (!availableSources.includes(source)) {
            return (
              <Chip
                key={source}
                label={SOURCE_LABEL[source]}
                size="small"
                disabled
                icon={<Cancel />}
                sx={ICON_INHERIT_SX}
              />
            );
          }
          const color = sourceColor[source];
          return (
            <Chip
              key={source}
              label={SOURCE_LABEL[source]}
              size="small"
              icon={<CheckCircle />}
              sx={{ color, bgcolor: alpha(color, 0.08), ...ICON_INHERIT_SX }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}
